import React, { useEffect, useState, useMemo } from 'react';

import {
  StyleSheet,
  View,
  Text,
  Image,
  ScrollView,
  TouchableOpacity,
  Platform,
  useWindowDimensions,
  ActivityIndicator,
} from 'react-native-web';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { useDispatch, useSelector } from 'react-redux';
import {
  setShowUnitSelectionScreen,
  setCourseData,
  setCurrentLessonProgress,
} from '../../../../store/course';
import { QUIKLEARN_BACKEND_URL, TOKEN_GENERATION_API, API_KEY } from 'config/app.config';
import { buildWebLaunchLocation, getWebAccessToken } from 'utils/webAuthSession';
import { showQuotaPaywall } from 'store/subscriptionUi';
import { unitRefusal } from './unitRefusal';

const PROGRESS_TEXT_DARK_SHADE = '#000000';
const PROGRESS_TEXT_LIGHT_SHADE = '#FFFFFF';
const QUIZ_TEXT_LIGHT_SHADE = '#FFFFFF';
const TEXT_SHADE_DARK = 'rgba(0,0,0,0.85)';
const TEXT_SHADE_LIGHT = 'rgba(255,255,255,0.85)';

const getProgressTextShadeStyle = (isDarkText) => ({
  textShadowColor: isDarkText ? TEXT_SHADE_LIGHT : TEXT_SHADE_DARK,
  textShadowOffset: { width: 0, height: 1 },
  textShadowRadius: 2,
});

const UnitSelectionOverlay = () => {
  const { width, height } = useWindowDimensions();
  const dispatch = useDispatch();

  const [headerHeight, setHeaderHeight] = useState(0);
  const [isTeleporting, setIsTeleporting] = useState(false);
  // Only a real failure to open a unit is shown inline; a plan boundary is the upgrade popup.
  const [lockedNotice, setLockedNotice] = useState('');
  const { courseData, loading } = useSelector(state => state.course);
  const showUnitSelectionScreen = useSelector(
    state => state.course.showUnitSelectionScreen,
  );
  const courseTokenValues = useSelector(state => state.app.courseTokenValues);
  const avatarUrl = useSelector(state => state.meeting.meeting.avatarUrlValue);

  const currentUnitId = courseTokenValues?.unitId;
  const userId = courseTokenValues?.userId;

  const units = useMemo(() => courseData?.units || [], [courseData]);
  // A notice belongs to the course it happened in (it used to follow the learner to other courses).
  useEffect(() => setLockedNotice(''), [courseData?.curriculumId]);
  const courseTitle = courseData?.courseTitle || '';

  // Calculate completed units (all lessons 100% completed)
  const completedCount = useMemo(() => {
    return units.filter(u => {
      const total = Number(u?.totalLessons ?? u?.lessons?.length ?? 0) || 0;
      const completed = Number(u?.hundredPercentCompletedCount ?? 0) || 0;
      return total > 0 && completed >= total;
    }).length;
  }, [units]);

  // Calculate mastered units (unit mastery >= 90)
  const masteredUnitsCount = useMemo(() => {
    return units.filter(u => {
      const mastery = Math.max(0, Math.min(100, u.unitMasteryPercentage || 0));
      return mastery >= 90;
    }).length;
  }, [units]);

  // Overall units progress percentage (for the header progress bar)
  const overallUnitsPct =
    units.length > 0 ? (completedCount / units.length) * 100 : 0;
  const overallUnitsMasteredPct =
    units.length > 0 ? (masteredUnitsCount / units.length) * 100 : 0;

  if (!showUnitSelectionScreen) return null;

  const handleClose = () => {
    if (isTeleporting) return; // Prevent closing while teleporting
    dispatch(setShowUnitSelectionScreen(false));
  };

  // Check if unit is current unit
  const isCurrentUnit = unit => {
    if (!currentUnitId || !unit?.id) return false;
    const normalize = (s = '') =>
      (s || '').toString().toLowerCase().replace(/[^a-z0-9]/g, '');
    return normalize(unit.id) === normalize(currentUnitId);
  };

  // Desktop card sizing - wider to accommodate 3 columns
  const cardWidth = Math.min(960, width - 48);
  const cardMaxHeight = Math.round(height * 0.90);
  const listMaxHeight = Math.max(200, cardMaxHeight - Math.max(0, headerHeight));

  // Calculate number of columns based on card width
  const unitCardMinWidth = 260;
  const gridGap = 16;
  const availableGridWidth = cardWidth - 32;
  const numColumns = Math.max(1, Math.min(3, Math.floor((availableGridWidth + gridGap) / (unitCardMinWidth + gridGap))));

  return (
    <View style={styles.overlay}>
      {/* Blur/Glass backdrop - clickable to close */}
      <TouchableOpacity
        style={styles.backdrop}
        activeOpacity={1}
        onPress={handleClose}
        disabled={isTeleporting}
        accessibilityLabel="Close modal"
      />

      {/* Center Card */}
      <View
        style={[
          styles.card,
          { width: cardWidth, maxHeight: cardMaxHeight },
        ]}>
        {lockedNotice ? (
          <View style={styles.lockedNotice}>
            <Text style={styles.lockedNoticeText}>{lockedNotice}</Text>
          </View>
        ) : null}

        {/* Teleporting Overlay */}
        {isTeleporting && (
          <View style={styles.teleportingOverlay}>
            <ActivityIndicator color="#C5E836" size="large" />
            <Text style={styles.teleportingText}>Teleporting…</Text>
          </View>
        )}

        {/* Header */}
        <View
          onLayout={e => setHeaderHeight(e?.nativeEvent?.layout?.height || 0)}
          style={styles.header}>
          {/* Close button */}
          <TouchableOpacity
            onPress={handleClose}
            disabled={isTeleporting}
            style={[styles.closeButton, isTeleporting && styles.disabledButton]}
            hitSlop={{ top: 16, bottom: 16, left: 16, right: 16 }}
            accessibilityLabel="Close">
            <Icon name="close" size={20} color="#fff" />
          </TouchableOpacity>

          {/* Course Title - hide during loading to avoid stale data */}
          {!loading && courseTitle ? (
            <Text style={styles.courseTitle} numberOfLines={1}>
              {courseTitle}
            </Text>
          ) : null}

          {/* Title */}
          <Text style={styles.title}>Select Unit</Text>
          {/* Subtitle */}
          <Text style={styles.subtitle}>
            Teleport to a world to start your adventure
          </Text>

          {/* Overall Progress Bar - hide during loading */}
          {!loading && (
            <>
              {/* Units completed (completion) */}
              <View style={styles.overallProgressBar}>
                <View
                  style={[
                    styles.overallProgressFillCompleted,
                    { width: `${overallUnitsPct}%` },
                  ]}
                />
                <View style={styles.progressTextContainer}>
                  <Text
                    style={[
                      styles.progressText,
                      {
                        color:
                          overallUnitsPct >= 50
                            ? PROGRESS_TEXT_DARK_SHADE
                            : PROGRESS_TEXT_LIGHT_SHADE,
                      },
                      getProgressTextShadeStyle(overallUnitsPct >= 50),
                    ]}>
                    {`${completedCount}/${units.length} units completed`}
                  </Text>
                </View>
              </View>

              {/* Units mastered (mastery) */}
              <View style={[styles.overallProgressBar, { marginTop: 10 }]}>
                <View
                  style={[
                    styles.overallProgressFillMastered,
                    { width: `${overallUnitsMasteredPct}%` },
                  ]}
                />
                <View style={styles.progressTextContainer}>
                  <Text
                    style={[
                      styles.progressText,
                      { color: QUIZ_TEXT_LIGHT_SHADE },
                      getProgressTextShadeStyle(false),
                    ]}>
                    {`${masteredUnitsCount}/${units.length} units with high quiz scores`}
                  </Text>
                </View>
              </View>
            </>
          )}
        </View>

        {/* Content Area with visible scrollbar */}
        <ScrollView
          style={[
            { maxHeight: listMaxHeight },
            Platform.OS === 'web' && styles.scrollViewWeb,
          ]}
          contentContainerStyle={styles.listContent}
          showsVerticalScrollIndicator={true}>

          {/* Loading state - show when loading, even if previous data exists */}
          {loading ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator color="#C5E836" size="large" />
              <Text style={styles.loadingText}>Loading units…</Text>
            </View>
          ) : units.length === 0 ? (
            /* No units */
            <Text style={styles.noUnitsText}>
              No units were found for this subject and grade.
            </Text>
          ) : (
            /* Units Grid - 3 columns on desktop */
            <View
              style={[
                styles.unitsGrid,
                Platform.OS === 'web' && {
                  display: 'grid',
                  gridTemplateColumns: `repeat(${numColumns}, 1fr)`,
                  gap: gridGap,
                },
              ]}>
              {units.map(unit => (
                <UnitCard
                  key={unit.id}
                  unit={unit}
                  isCurrent={isCurrentUnit(unit)}
                  courseData={courseData}
                  avatarUrl={avatarUrl}
                  userId={userId}
                  dispatch={dispatch}
                  isTeleporting={isTeleporting}
                  setIsTeleporting={setIsTeleporting}
                  setLockedNotice={setLockedNotice}
                />
              ))}
            </View>
          )}
        </ScrollView>
      </View>
    </View>
  );
};

// UnitCard Component
const UnitCard = ({
  unit,
  isCurrent,
  courseData,
  avatarUrl,
  userId,
  dispatch,
  isTeleporting,
  setIsTeleporting,
  // The notice lives on the overlay so one message shows for the whole list
  // rather than one per card; the setter has to be handed down with it.
  setLockedNotice,
}) => {
  const completion = Math.max(
    0,
    Math.min(100, unit.lessonsCompletedPercentage || 0),
  );
  const lessonsCount =
    Number(unit?.totalLessons ?? unit?.lessons?.length ?? unit?.numberOfLessons ?? 0) || 0;
  const quizzesPassedCount = Math.max(
    0,
    Math.min(lessonsCount, Number(unit?.lessonsMastered ?? 0) || 0),
  );

  // Token generation helper
  const generateMetaverseToken = async (uid, curriculumId, unitId) => {
    const base = String(QUIKLEARN_BACKEND_URL || '').replace(/\/$/, '');
    const endpoint = String(TOKEN_GENERATION_API || '');
    const url = `${base}${endpoint.startsWith('/') ? '' : '/'}${endpoint}`;
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': API_KEY,
          Authorization: `Bearer ${getWebAccessToken() || ''}`,
        },
        body: JSON.stringify({
          userId: uid,
          curriculumId,
          unitId,
        }),
      });
      if (!response.ok) {
        // Preserve the refusal: a 402 carries the reason this plan may not open the
        // unit, and discarding it leaves the learner with a button that silently fails.
        let body = {};
        try {
          body = await response.json();
        } catch (_) {
          body = {};
        }
        const refusal = new Error(body?.message || 'Failed to generate metaverse token');
        refusal.status = response.status;
        refusal.code = body?.details?.code || body?.code || '';
        throw refusal;
      }
      const data = await response.json();
      return data.token;
    } catch (error) {
      console.error('Error generating metaverse token:', error);
      throw error;
    }
  };

  const handleTeleport = async () => {
    if (isTeleporting) return; // Prevent multiple clicks
    setLockedNotice('');

    setIsTeleporting(true);

    try {
      // Preselect first lesson of this unit as current context
      try {
        const firstLesson = unit?.lessons?.[0];
        if (firstLesson && courseData) {
          dispatch(
            setCourseData({
              ...courseData,
              currentLessonId: firstLesson.lessonId,
              currentLessonTotalSlides: firstLesson.totalSlides,
            }),
          );
          dispatch(
            setCurrentLessonProgress({
              currentLessonId: firstLesson.lessonId,
              currentLessonProgressPercentage: Number(firstLesson.progress) || 0,
              currentLessonTotalSlides: Number(firstLesson.totalSlides) || 0,
              currentLessonMasteryScore: Number(firstLesson.masteryScore) || 0,
            }),
          );
        }
      } catch (e) {
        console.warn('[UnitSelectionOverlay] Failed to preset first lesson', e);
      }

      const curriculumId = courseData.curriculumId;
      const unitId = unit.id;
      const token = await generateMetaverseToken(userId, curriculumId, unitId);

      window.location.href = buildWebLaunchLocation(window.location.origin, token, {
        avatarSelection: false,
        legacy: false,
        avatarUrl,
      });
    } catch (error) {
      console.error('Redirect failed due to token generation error:', error);
      const refusal = unitRefusal(error);
      if (refusal.paywall) dispatch(showQuotaPaywall(refusal.paywall));
      else setLockedNotice(refusal.notice);
      setIsTeleporting(false); // Re-enable on error
    }
  };

  return (
    <View
      style={[
        styles.unitCard,
        isCurrent && styles.unitCardCurrent,
      ]}>
      {/* Unit Image with Badges */}
      <View style={styles.imageContainer}>
        <Image
          source={{ uri: unit.unitImage }}
          style={styles.unitImage}
          resizeMode="cover"
        />

        {/* Badges overlay */}
        <View style={styles.badgesContainer}>
          <View style={styles.unitBadge}>
            <Text style={styles.unitBadgeText}>Unit {unit.unitNumber}</Text>
          </View>
          <View style={styles.lessonsBadge}>
            <Icon name="book-open-variant" size={12} color="#000" />
            <Text style={styles.lessonsBadgeText}>{lessonsCount} Lessons</Text>
          </View>
        </View>

        {/* NOW badge for current unit */}
        {isCurrent && (
          <View style={styles.nowBadge}>
            <Text style={styles.nowBadgeText}>NOW</Text>
          </View>
        )}
      </View>

      {/* Unit Info */}
      <View style={styles.unitContent}>
        {/* Unit Title */}
        <Text style={styles.unitTitle} numberOfLines={2}>
          {unit.title}
        </Text>

        {/* Unit completed (completion) */}
        <View style={styles.completionProgressBar}>
          <View
            style={[styles.completionProgressFill, { width: `${completion}%` }]}
          />
          <View style={styles.progressTextContainer}>
            <Text
              style={[
                styles.completionProgressText,
                {
                  color:
                    completion >= 50
                      ? PROGRESS_TEXT_DARK_SHADE
                      : PROGRESS_TEXT_LIGHT_SHADE,
                },
                getProgressTextShadeStyle(completion >= 50),
              ]}>
              {`${completion}% lessons completed`}
            </Text>
          </View>
        </View>

        {/* Unit mastered (quizzes passed out of total — count-based, not a percentage) */}
        <View style={styles.masteryProgressBar}>
          <View
            style={[
              styles.masteryProgressFill,
              {
                width: `${
                  lessonsCount > 0
                    ? Math.round((quizzesPassedCount / lessonsCount) * 100)
                    : 0
                }%`,
              },
            ]}
          />
          <View style={styles.progressTextContainer}>
            <Text
              style={[
                styles.masteryProgressText,
                { color: QUIZ_TEXT_LIGHT_SHADE },
                getProgressTextShadeStyle(false),
              ]}>
              {`${quizzesPassedCount}/${lessonsCount} quizzes passed`}
            </Text>
          </View>
        </View>

        {/* Teleport Button */}
        <TouchableOpacity
          activeOpacity={0.85}
          onPress={handleTeleport}
          disabled={isTeleporting}
          style={[
            styles.teleportButton,
            isTeleporting && styles.teleportButtonDisabled,
          ]}>
          <Icon name="flash" size={16} color={isTeleporting ? '#666' : '#000'} />
          <Text
            style={[
              styles.teleportButtonText,
              isTeleporting && styles.teleportButtonTextDisabled,
            ]}>
            {isTeleporting ? 'Teleporting…' : 'Teleport'}
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  lockedNotice: {
    marginHorizontal: 16,
    marginTop: 12,
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 10,
    backgroundColor: 'rgba(214, 158, 46, 0.14)',
    borderWidth: 1,
    borderColor: 'rgba(214, 158, 46, 0.45)',
  },
  lockedNoticeText: {
    color: '#F6E4BC',
    fontSize: 14,
    lineHeight: 20,
    textAlign: 'center',
  },
  overlay: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 9999,
    justifyContent: 'center',
    alignItems: 'center',
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.70)',
    ...(Platform.OS === 'web' && {
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
    }),
  },
  card: {
    backgroundColor: '#111216',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.10)',
    overflow: 'hidden',
    position: 'relative',
    ...(Platform.OS === 'web' && {
      boxShadow: '0 12px 40px rgba(0, 0, 0, 0.6)',
    }),
  },
  teleportingOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(17, 18, 22, 0.90)',
    zIndex: 100,
    alignItems: 'center',
    justifyContent: 'center',
  },
  teleportingText: {
    color: '#C5E836',
    fontSize: 16,
    fontWeight: '600',
    marginTop: 16,
  },
  header: {
    padding: 24,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.10)',
    alignItems: 'center',
  },
  closeButton: {
    position: 'absolute',
    right: 16,
    top: 16,
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0.7,
    zIndex: 10,
  },
  disabledButton: {
    opacity: 0.3,
  },
  courseTitle: {
    color: '#C5E836',
    fontWeight: '600',
    fontSize: 14,
    marginBottom: 4,
    textTransform: 'uppercase',
    letterSpacing: 1,
    textAlign: 'center',
  },
  title: {
    color: '#fff',
    fontWeight: '700',
    fontSize: 24,
    marginBottom: 4,
    textAlign: 'center',
  },
  subtitle: {
    color: 'rgba(255, 255, 255, 0.50)',
    fontSize: 14,
    marginBottom: 20,
    textAlign: 'center',
  },
  overallProgressBar: {
    height: 28,
    borderRadius: 14,
    backgroundColor: 'rgba(255, 255, 255, 0.10)',
    overflow: 'hidden',
    position: 'relative',
    alignSelf: 'stretch',
  },
  overallProgressFillCompleted: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    backgroundColor: '#fbbf24',
    borderRadius: 14,
  },
  overallProgressFillMastered: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    backgroundColor: '#8B5CF6',
    borderRadius: 14,
  },
  progressTextContainer: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
  },
  progressText: {
    fontSize: 10,
    fontWeight: '700',
  },
  scrollViewWeb: {
    ...(Platform.OS === 'web' && {
      overflowY: 'auto',
      scrollbarWidth: 'thin',
      scrollbarColor: '#C5E836 rgba(255, 255, 255, 0.1)',
    }),
  },
  listContent: {
    padding: 16,
  },
  loadingContainer: {
    padding: 60,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingText: {
    color: 'rgba(255, 255, 255, 0.70)',
    fontSize: 14,
    textAlign: 'center',
    marginTop: 16,
  },
  noUnitsText: {
    color: 'rgba(255, 255, 255, 0.70)',
    fontSize: 14,
    textAlign: 'center',
    padding: 40,
  },
  unitsGrid: {
    // Grid styles applied inline for web
  },
  unitCard: {
    borderRadius: 12,
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderWidth: 1,
    borderColor: 'transparent',
    overflow: 'hidden',
  },
  unitCardCurrent: {
    backgroundColor: 'rgba(197, 232, 54, 0.10)',
    borderColor: 'rgba(197, 232, 54, 0.30)',
  },
  imageContainer: {
    position: 'relative',
    height: 120,
    overflow: 'hidden',
  },
  unitImage: {
    width: '100%',
    height: '100%',
  },
  badgesContainer: {
    position: 'absolute',
    bottom: 10,
    left: 10,
    flexDirection: 'row',
    gap: 8,
  },
  unitBadge: {
    paddingHorizontal: 10,
    paddingVertical: 3,
    backgroundColor: '#C5E836',
    borderRadius: 6,
  },
  unitBadgeText: {
    color: '#000',
    fontSize: 11,
    fontWeight: '700',
  },
  lessonsBadge: {
    paddingHorizontal: 10,
    paddingVertical: 3,
    backgroundColor: 'rgba(255, 255, 255, 0.90)',
    borderRadius: 6,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  lessonsBadgeText: {
    color: '#000',
    fontSize: 11,
    fontWeight: '500',
  },
  nowBadge: {
    position: 'absolute',
    top: 10,
    right: 10,
    paddingHorizontal: 8,
    paddingVertical: 3,
    backgroundColor: 'rgba(239, 68, 68, 0.90)',
    borderRadius: 6,
  },
  nowBadgeText: {
    color: '#fff',
    fontSize: 11,
    fontWeight: '700',
  },
  unitContent: {
    padding: 14,
  },
  unitTitle: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 18,
    marginBottom: 10,
    minHeight: 36,
  },
  masteryProgressBar: {
    marginBottom: 12,
    height: 18,
    borderRadius: 9,
    backgroundColor: 'rgba(255, 255, 255, 0.10)',
    overflow: 'hidden',
    position: 'relative',
  },
  masteryProgressFill: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    backgroundColor: '#8B5CF6',
    borderRadius: 9,
  },
  masteryProgressText: {
    fontSize: 10,
    fontWeight: '700',
  },
  completionProgressBar: {
    marginBottom: 12,
    height: 18,
    borderRadius: 9,
    backgroundColor: 'rgba(255, 255, 255, 0.10)',
    overflow: 'hidden',
    position: 'relative',
  },
  completionProgressFill: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    backgroundColor: '#fbbf24',
    borderRadius: 9,
  },
  completionProgressText: {
    fontSize: 10,
    fontWeight: '700',
  },
  teleportButton: {
    height: 36,
    borderRadius: 8,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    ...(Platform.OS === 'web' && {
      backgroundImage: 'linear-gradient(to right, #C5E836, #84cc16)',
      cursor: 'pointer',
    }),
    backgroundColor: '#C5E836',
  },
  teleportButtonDisabled: {
    ...(Platform.OS === 'web' && {
      backgroundImage: 'none',
      cursor: 'not-allowed',
    }),
    backgroundColor: '#333',
  },
  teleportButtonText: {
    color: '#000',
    fontSize: 13,
    fontWeight: '700',
  },
  teleportButtonTextDisabled: {
    color: '#666',
  },
});

export default UnitSelectionOverlay;
